import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { restClient } from './RestClient';

interface SignUpProps {
  onSignUp: (token: string, username: string, isAdmin: boolean, isSuperUser: boolean) => void;
  onSwitchToLogin: () => void;
}

interface SignUpResponse {
  token: string;
  username: string;
  nickname: string;
  userId: number;
  expiresAt: string;
  message: string;
  isAdmin: boolean;
  isSuperUser: boolean;
}

export function SignUp({ onSignUp, onSwitchToLogin }: SignUpProps) {
  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [teamName, setTeamName] = useState('');
  const [sport, setSport] = useState('Football');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);

  const addError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 8000);
  };

  const addSuccess = (message: string) => {
    const success = createErrorMessage(message, 'info');
    setErrors(prev => [...prev, success]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== success.id));
    }, 5000);
  };

  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  const validateForm = (): boolean => {
    if (!username.trim()) {
      addError('Please enter an email address');
      return false;
    }

    // Email format validation - prevents consecutive dots, requires valid TLD
    const emailRegex = /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(username.trim())) {
      addError('Please enter a valid email address');
      return false;
    }

    if (!nickname.trim()) {
      addError('Please enter a nickname');
      return false;
    }

    if (nickname.trim().length < 2 || nickname.trim().length > 30) {
      addError('Nickname must be between 2 and 30 characters');
      return false;
    }

    const nicknameRegex = /^[A-Za-z0-9 -]+$/;
    if (!nicknameRegex.test(nickname.trim())) {
      addError('Nickname can only contain letters, numbers, spaces, and hyphens');
      return false;
    }

    if (!teamName.trim()) {
      addError('Please enter a team name');
      return false;
    }

    if (teamName.trim().length < 2 || teamName.trim().length > 20) {
      addError('Team name must be between 2 and 20 characters');
      return false;
    }

    const teamNameRegex = /^[A-Za-z0-9 -]+$/;
    if (!teamNameRegex.test(teamName.trim())) {
      addError('Team name can only contain letters, numbers, spaces, and hyphens');
      return false;
    }

    if (!password) {
      addError('Please enter a password');
      return false;
    }

    if (password.length < 6) {
      addError('Password must be at least 6 characters long');
      return false;
    }

    if (!confirmPassword) {
      addError('Please confirm your password');
      return false;
    }

    if (password !== confirmPassword) {
      addError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await restClient.signup(username.trim(), password, nickname.trim(), teamName.trim(), sport);

      if (result.IsSuccess) {
        const data: SignUpResponse = result.Value;
        addSuccess(data.message || 'Account created successfully!');

        // Store auth data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('nickname', data.nickname);
        localStorage.setItem('tokenExpiry', data.expiresAt);
        localStorage.setItem('isAdmin', data.isAdmin.toString());
        localStorage.setItem('isSuperUser', data.isSuperUser.toString());

        // Delay login to show success message
        setTimeout(() => {
          onSignUp(data.token, data.username, data.isAdmin, data.isSuperUser);
        }, 1500);
      } else {
        // Handle specific error cases
        switch (result.Error.code) {
          case 'USER_EXISTS':
            addError('This email is already registered. Please use a different email or sign in.');
            break;
          case 'WEAK_PASSWORD':
            addError(result.Error.description || 'Password is too weak.');
            break;
          case 'INVALID_EMAIL':
            addError('Please provide a valid email address.');
            break;
          case 'INVALID_INPUT':
            addError(result.Error.description || 'Please check your input.');
            break;
          default:
            addError(result.Error.description || 'Sign-up failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Sign-up error:', error);
      addError('Unable to connect to server. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ErrorNotification errors={errors} onDismiss={dismissError} />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo auth-logo-signup">
            <UserPlus size={28} />
          </div>
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Join Team Selection today</p>
        </div>

        <form onSubmit={handleSubmit} className="mb-6">
          <div className="form-group-lg">
            <label className="form-label">Email</label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your email address"
              disabled={isLoading}
              className="form-control form-control-lg form-control-success"
            />
          </div>

          <div className="form-group-lg">
            <label className="form-label">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Choose a display name (2-30 characters)"
              disabled={isLoading}
              className="form-control form-control-lg form-control-success"
            />
          </div>

          <div className="form-group-lg">
            <label className="form-label">Team Name</label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter your team name (2-20 characters)"
              disabled={isLoading}
              className="form-control form-control-lg form-control-success"
            />
          </div>

          <div className="form-group-lg">
            <label className="form-label">Sport</label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              disabled={isLoading}
              className="form-select form-control-lg"
            >
              <option value="Football">Football</option>
              <option value="Rugby">Rugby</option>
            </select>
          </div>

          <div className="form-group-lg">
            <label className="form-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 6 characters)"
              disabled={isLoading}
              className="form-control form-control-lg form-control-success"
            />
          </div>

          <div className="form-group-lg">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={isLoading}
              className="form-control form-control-lg form-control-success"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`btn btn-lg btn-full ${isLoading ? 'btn-secondary' : 'btn-success'}`}
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p className="auth-footer-text">Already have an account?</p>
          <button
            onClick={onSwitchToLogin}
            disabled={isLoading}
            className="btn-link"
            style={{ opacity: isLoading ? 0.5 : 1 }}
          >
            Sign in here
          </button>
        </div>
      </div>
    </div>
  );
}
